import React, { useEffect, useState } from 'react';
import Layout from './components/Layout';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { applyChurchBranding, resetChurchBranding } from './utils/churchBranding';
import { applyAccountLanguage } from './utils/language';
import { FINANCE_DOMAIN_ROLES, effectiveRolesOf } from './utils/roles';
import i18n from './i18n';

const Login           = React.lazy(() => import('./pages/Login'));
const ChangePassword  = React.lazy(() => import('./pages/ChangePassword'));
const Dashboard       = React.lazy(() => import('./pages/Dashboard'));
const SuperAdmin      = React.lazy(() => import('./pages/SuperAdmin'));
const Members         = React.lazy(() => import('./pages/Members'));
// MemberDetail은 더 이상 별도 라우트로 쓰지 않음 — Members.jsx가 교인 목록+상세를
// 좌우 분할 화면으로 함께 렌더링하면서 그 안에서 직접 import해 사용함
// (아래 /members/:id 라우트도 Members를 그대로 렌더링).
const CodeSettings    = React.lazy(() => import('./pages/CodeSettings'));
const Users           = React.lazy(() => import('./pages/Users'));
const Attendance      = React.lazy(() => import('./pages/Attendance'));
const AttendanceCheck = React.lazy(() => import('./pages/AttendanceCheck'));
const Groups          = React.lazy(() => import('./pages/Groups'));
const GroupDetail     = React.lazy(() => import('./pages/GroupDetail'));
const GroupCompareMove = React.lazy(() => import('./pages/GroupCompareMove'));
const Events          = React.lazy(() => import('./pages/Events'));
const EventDetail     = React.lazy(() => import('./pages/EventDetail'));
const Finance         = React.lazy(() => import('./pages/Finance'));
const Photos          = React.lazy(() => import('./pages/Photos'));
const ChurchHistory   = React.lazy(() => import('./pages/ChurchHistory'));
const Pastoral             = React.lazy(() => import('./pages/Pastoral'));
const MeetingMinutes       = React.lazy(() => import('./pages/MeetingMinutes'));
const Committee            = React.lazy(() => import('./pages/Committee'));
const CommitteeEquipmentScan = React.lazy(() => import('./pages/CommitteeEquipmentScan'));
const CommitteeLocationScan  = React.lazy(() => import('./pages/CommitteeLocationScan'));
const PrayerRelay          = React.lazy(() => import('./pages/PrayerRelay'));
const MeetingRoomReservation = React.lazy(() => import('./pages/MeetingRoomReservation'));
const Register             = React.lazy(() => import('./pages/Register'));
const Welcome              = React.lazy(() => import('./pages/Welcome'));
const CardScan             = React.lazy(() => import('./pages/CardScan'));
const Checkin              = React.lazy(() => import('./pages/Checkin'));
const PendingRegistrations = React.lazy(() => import('./pages/PendingRegistrations'));
const QRCode              = React.lazy(() => import('./pages/QRCode'));
const MyQR                = React.lazy(() => import('./pages/MyQR'));


// ── 스플래시 화면 (교회 맞춤형) ─────────────────────────────────────────
// 로그인 이력이 있으면 캐시된 user.church.name_ko(또는 name_en)를 가장 크게
// 보여주고, 처음 방문했거나 로그아웃 상태라 캐시된 교회 정보가 없으면
// "할라크" 자체를 크게 보여주는 것으로 자연스럽게 대체한다.
function SplashScreen() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const churchName = user?.church?.name_ko || user?.church?.name_en || null;
  const [logoOk, setLogoOk] = useState(true);

  return (
    <div style={{
      minHeight: '100vh', width: '100%', position: 'relative', overflow: 'hidden',
      background: 'radial-gradient(circle at 50% 35%, #6b5fd4 0%, #534AB7 55%, #423b93 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <style>{`
        @keyframes halakFadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes halakFadeIn { from { opacity:0; } to { opacity:.85; } }
        @keyframes halakBounce { 0%,60%,100% { transform:translateY(0); opacity:.5; } 30% { transform:translateY(-5px); opacity:1; } }
      `}</style>

      <div style={{ position: 'absolute', width: 420, height: 420, top: -160, left: -140, borderRadius: '50%', background: '#ffffff12' }} />
      <div style={{ position: 'absolute', width: 320, height: 320, bottom: -140, right: -100, borderRadius: '50%', background: '#ffffff12' }} />

      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, padding: '0 32px', textAlign: 'center' }}>
        <div style={{
          color: '#fff', fontSize: churchName ? 34 : 30, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.3,
          opacity: 0, animation: 'halakFadeUp .65s cubic-bezier(.2,.9,.25,1) .1s forwards',
          textShadow: '0 4px 24px rgba(0,0,0,.18)',
        }}>
          {churchName || '할라크'}
        </div>
        <div style={{
          color: '#ffffffb0', fontSize: 15, fontWeight: 600, letterSpacing: '.12em',
          opacity: 0, animation: 'halakFadeUp .55s ease-out .4s forwards',
        }}>
          {churchName ? 'HALAK · 할라크' : i18n.t('app.splashTagline')}
        </div>
        <div style={{ display: 'flex', gap: 7, marginTop: 8, opacity: 0, animation: 'halakFadeUp .5s ease-out .65s forwards' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ffffff88', animation: 'halakBounce 1.1s ease-in-out infinite' }} />
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ffffff88', animation: 'halakBounce 1.1s ease-in-out .15s infinite' }} />
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ffffff88', animation: 'halakBounce 1.1s ease-in-out .3s infinite' }} />
        </div>
      </div>

      {logoOk && (
        <img
          src="/logo-sov.png"
          alt="Sense of Value Studio"
          onError={() => setLogoOk(false)}
          style={{
            position: 'absolute', bottom: 34, left: '50%', transform: 'translateX(-50%)',
            width: 62, height: 'auto', opacity: 0,
            animation: 'halakFadeIn .6s ease-out .85s forwards',
            filter: 'drop-shadow(0 3px 10px rgba(0,0,0,.2))',
          }}
        />
      )}
    </div>
  );
}

// ── 라우트 가드 ──────────────────────────────────────────────────────────
const PrivateRoute = ({ children }) => {
  if (!localStorage.getItem('token')) return <Navigate to="/login" replace />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  if (!localStorage.getItem('token')) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (!user.is_super_admin) return <Navigate to="/" replace />;
  return children;
};

const ChurchRoute = ({ children }) => {
  if (!localStorage.getItem('token')) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.is_super_admin) return <Navigate to="/super-admin" replace />;
  return <Layout>{children}</Layout>;
};

// 2026-09: "장소 QR을 스캔하면 사이드바를 표시할 필요 없어요" — 비품 실사(QR 스캔)
// 화면들은 사무실 책상이 아니라 폰을 들고 방을 돌아다니며 여는 화면이라, 데스크톱용
// 좌측 메뉴(Layout)가 화면 절반을 차지하면 오히려 방해가 된다. 로그인 여부/최고관리자
// 리다이렉트 같은 접근 제어는 ChurchRoute와 동일하게 하되, <Layout>으로 감싸지 않고
// 화면 전체를 그대로 쓴다.
const ScanRoute = ({ children }) => {
  if (!localStorage.getItem('token')) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.is_super_admin) return <Navigate to="/super-admin" replace />;
  return children;
};

// 2026-09: "/"(대시보드)는 교인 수/출석률/주일학교·구역/다가오는 행사 요약을 보여준다.
// 재정 "관리자" 성격의 계정(deacon_admin/jikje_admin/finance — 재정 업무가 본업이라
// 헌금/재정 화면이 기본 화면이어야 함)은 여기로 보내지 않고 곧장 /finance로 보낸다.
// 안수집사(deacon)/제직(jikje)은 조회 전용 그룹이라 대시보드의 "요약" 정도는 볼 수
// 있게 해 달라는 요청(2026-09)에 따라 대시보드는 그대로 보여주되, 거기서 교인/출석/
// 그룹/행사 "상세"로 더 들어가는 것은 Dashboard.jsx와 PastoralOnlyRoute가 막는다.
const FINANCE_MANAGER_ROLES = ['deacon_admin', 'jikje_admin', 'finance'];
const HomeRoute = () => {
  if (!localStorage.getItem('token')) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.is_super_admin) return <Navigate to="/super-admin" replace />;
  // 2026-09: 재정 진영 세부 역할 겸임 기능(migrations/090) — 겸임 중인 역할 중 하나라도
  // deacon/jikje(대시보드를 볼 수 있는 조회 전용 그룹)이면 대시보드로 보낸다. 가진
  // 역할 "전부가" 재정 관리자(deacon_admin/jikje_admin) 또는 finance 전담일 때만
  // /finance로 곧장 보낸다 — 겸임 없는 계정은 effectiveRoles가 [role] 하나뿐이라
  // 기존과 동일하게 동작한다.
  const roles = effectiveRolesOf(user);
  if (roles.length && roles.every((r) => FINANCE_MANAGER_ROLES.includes(r))) return <Navigate to="/finance" replace />;
  return <Layout><Dashboard /></Layout>;
};

// 교인/출석/그룹/행사 "상세" 화면 — 재정 진영(deacon_admin/jikje_admin/finance/deacon/
// jikje) 전체는 여기 들어올 수 없다. 안수집사/제직은 대시보드에서 요약은 보되 "전체
// 보기"·항목 클릭으로 여기까지 진전되지 않도록 Dashboard.jsx에서 그 버튼들 자체를
// 이미 감췄지만, 주소창에 직접 쳐서 들어오는 경로도 서버(routes/members.js 등)와
// 함께 여기서 한 번 더 막는다.
const PastoralOnlyRoute = ({ children }) => {
  if (!localStorage.getItem('token')) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (user.is_super_admin) return <Navigate to="/super-admin" replace />;
  if (FINANCE_DOMAIN_ROLES.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

export default function App() {

  // ── 앱 시작 시 교회 아이콘/이름 + 언어 적용 ─────────────────────────────
  // 파비콘/홈 화면 아이콘은 소속 교회 것으로, 화면 언어는 "내가 직접 고른 언어
  // (계정에 저장됨) → 없으면 소속 교회의 기본 언어 → 그래도 없으면 한국어" 순으로 정한다.
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.church) {
      applyChurchBranding(user.church);
      applyAccountLanguage(user);
    }

    const onStorage = (e) => {
      if (e.key === 'user') {
        const updated = JSON.parse(e.newValue || '{}');
        if (updated?.church) {
          applyChurchBranding(updated.church);
          applyAccountLanguage(updated);
        } else {
          resetChurchBranding();
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // 앱 시작 시 스플래시 (2.8초 후 자동 전환)
  const [showSplash, setShowSplash] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 2800);
    return () => clearTimeout(t);
  }, []);

  return (
    <BrowserRouter>
      <React.Suspense fallback={<div style={{padding:'2rem',color:'#888'}}>Loading...</div>}>
        {/* 포털 페이지는 스플래시/인증 게이트 밖에서 처리 */}
        <Routes>
          <Route path="/my/:token" element={<MyQR />} />
          <Route path="/register/:churchId" element={<Register />} />
          <Route path="/welcome/:churchId" element={<Welcome />} />
          <Route path="*" element={
            showSplash ? <SplashScreen /> : (
              <Routes>
                <Route path="/login"           element={<Login />} />
                <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
                <Route path="/super-admin"     element={<SuperAdminRoute><SuperAdmin /></SuperAdminRoute>} />
                <Route path="/"                element={<HomeRoute />} />
                <Route path="/members"         element={<PastoralOnlyRoute><Members /></PastoralOnlyRoute>} />
                <Route path="/members/:id"     element={<PastoralOnlyRoute><Members /></PastoralOnlyRoute>} />
                <Route path="/attendance"      element={<PastoralOnlyRoute><Attendance /></PastoralOnlyRoute>} />
                <Route path="/attendance/:serviceId" element={<PastoralOnlyRoute><AttendanceCheck /></PastoralOnlyRoute>} />
                <Route path="/groups"          element={<PastoralOnlyRoute><Groups /></PastoralOnlyRoute>} />
                <Route path="/groups/:id"      element={<PastoralOnlyRoute><GroupDetail /></PastoralOnlyRoute>} />
                <Route path="/groups/:id/compare" element={<PastoralOnlyRoute><GroupCompareMove /></PastoralOnlyRoute>} />
                <Route path="/events"          element={<PastoralOnlyRoute><Events /></PastoralOnlyRoute>} />
                <Route path="/events/:id"      element={<PastoralOnlyRoute><EventDetail /></PastoralOnlyRoute>} />
                <Route path="/finance"         element={<ChurchRoute><Finance /></ChurchRoute>} />
                <Route path="/photos"          element={<ChurchRoute><Photos /></ChurchRoute>} />
                <Route path="/photos/:albumId" element={<ChurchRoute><Photos /></ChurchRoute>} />
                <Route path="/church-history" element={<ChurchRoute><ChurchHistory /></ChurchRoute>} />
                <Route path="/pastoral"             element={<ChurchRoute><Pastoral /></ChurchRoute>} />
                <Route path="/meeting-minutes"      element={<ChurchRoute><MeetingMinutes /></ChurchRoute>} />
                <Route path="/committee"            element={<ChurchRoute><Committee /></ChurchRoute>} />
                {/* 미팅룸 예약: 재정 진영(deacon_admin/jikje_admin/finance/deacon/jikje)은
                    제외 — committee/meetingMinutes와 달리 그 진영에게 열어줄 이유가 없는
                    일반 사역 메뉴라, Members/Groups와 동일하게 PastoralOnlyRoute를 쓴다. */}
                <Route path="/meeting-rooms"        element={<PastoralOnlyRoute><MeetingRoomReservation /></PastoralOnlyRoute>} />
                <Route path="/committee/equipment/:id" element={<ScanRoute><CommitteeEquipmentScan /></ScanRoute>} />
                <Route path="/committee/location/:departmentId/:code" element={<ScanRoute><CommitteeLocationScan /></ScanRoute>} />
                <Route path="/prayer-relay"          element={<ChurchRoute><PrayerRelay /></ChurchRoute>} />
                <Route path="/card-scan"           element={<ChurchRoute><CardScan /></ChurchRoute>} />
                <Route path="/checkin"             element={<ChurchRoute><Checkin /></ChurchRoute>} />
                <Route path="/registrations"       element={<ChurchRoute><PendingRegistrations /></ChurchRoute>} />
                <Route path="/qrcode"             element={<ChurchRoute><QRCode /></ChurchRoute>} />
                <Route path="/settings/codes"  element={<ChurchRoute><CodeSettings /></ChurchRoute>} />
                <Route path="/settings/users"  element={<ChurchRoute><Users /></ChurchRoute>} />
                <Route path="*"                element={<Navigate to="/" replace />} />
              </Routes>
            )
          } />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
}
